import { ActionItem, ActionType, EntityKind } from "../../../types";

export interface ActionCopy {
  metaKey: string;
  ctaKey: string;
  /** Short, count-free name for this action type — used to name what
   * degraded when a detector fails, not rendered on the row (the row
   * title is always "reference · subject"). */
  labelKey: string;
}

/**
 * The single place a typed action becomes something a person reads.
 *
 * The backend deliberately sends no strings, so copy changes never need a Java
 * deploy — and every key here must exist in all three locale files, because
 * react-i18next falls back to English silently and ESLint will not catch a gap.
 */
export const actionCopy: Record<ActionType, ActionCopy> = {
  [ActionType.AWARD_DECISION]: {
    metaKey: "managerDashboard.action.awardDecision.meta",
    ctaKey: "managerDashboard.action.awardDecision.cta",
    labelKey: "managerDashboard.action.awardDecision.label",
  },
  [ActionType.QUOTING_CLOSING]: {
    metaKey: "managerDashboard.action.quotingClosing.meta",
    ctaKey: "managerDashboard.action.quotingClosing.cta",
    labelKey: "managerDashboard.action.quotingClosing.label",
  },
  [ActionType.CONSULTATION_ENDING]: {
    metaKey: "managerDashboard.action.consultationEnding.meta",
    ctaKey: "managerDashboard.action.consultationEnding.cta",
    labelKey: "managerDashboard.action.consultationEnding.label",
  },
  [ActionType.NEVER_LAUNCHED]: {
    metaKey: "managerDashboard.action.neverLaunched.meta",
    ctaKey: "managerDashboard.action.neverLaunched.cta",
    labelKey: "managerDashboard.action.neverLaunched.label",
  },
  [ActionType.DELIVERY_UNCONFIRMED]: {
    metaKey: "managerDashboard.action.deliveryUnconfirmed.meta",
    ctaKey: "managerDashboard.action.deliveryUnconfirmed.cta",
    labelKey: "managerDashboard.action.deliveryUnconfirmed.label",
  },
  [ActionType.UNASSIGNED]: {
    metaKey: "managerDashboard.action.unassigned.meta",
    ctaKey: "managerDashboard.action.unassigned.cta",
    labelKey: "managerDashboard.action.unassigned.label",
  },
  [ActionType.STALLED_CREATED]: {
    metaKey: "managerDashboard.action.stalledCreated.meta",
    ctaKey: "managerDashboard.action.stalledCreated.cta",
    labelKey: "managerDashboard.action.stalledCreated.label",
  },
  [ActionType.TEAM_AWARD_OVERDUE]: {
    metaKey: "managerDashboard.action.teamAwardOverdue.meta",
    ctaKey: "managerDashboard.action.teamAwardOverdue.cta",
    labelKey: "managerDashboard.action.teamAwardOverdue.label",
  },
  [ActionType.TEAM_NO_BIDS]: {
    metaKey: "managerDashboard.action.teamNoBids.meta",
    ctaKey: "managerDashboard.action.teamNoBids.cta",
    labelKey: "managerDashboard.action.teamNoBids.label",
  },
};

/** Where a row takes you. Derived from the entity, not the action type. */
export const routeFor = (item: ActionItem): string => {
  switch (item.entityKind) {
    case EntityKind.ORDER:
      // A joint order routes to the joint view (its constituent order id as the path, the joint id
      // as the query param) so the whole RFQ opens, not a single line.
      return item.multipleOrderId
        ? `/orders/multiple/${item.entityId}?multipleOrderId=${item.multipleOrderId}`
        : `/orders/${item.entityId}`;
    case EntityKind.PURCHASE_REQUEST:
      return `/purchase-requests/${item.entityId}`;
    case EntityKind.DELIVERY:
      return `/deliveries/${item.entityId}`;
    default:
      return "/";
  }
};
